/**
 * 任务传输管理器：航线 / 围栏 / 备降点三种 `mission_type` 的上传与下载状态机。
 *
 * 这个模块存在的理由，是"上传成功"这四个字在 MAVLink 里**没有**任何一个字节直接表达它。
 * 能证明它的只有两件事：机体回的那条 `MISSION_ACK` 的结果码，以及随后能把航线原样读回来。
 * 所以这里的状态机不接受"发完就算成功"，也不把非 0 的结果码当成"完成"。
 *
 * 几个刻意的取舍：
 *
 * 1. **状态按 `mission_type` 分桶，互不干扰。** 航线、围栏、备降点在 MAVLink 里是三份
 *    独立的存储（`MAV_MISSION_TYPE`），机体可以同时有一份航线与一份围栏。
 *    用一个全局的"当前传输"会让"传围栏"把"传航线"顶掉，而界面上这两件事来自不同面板。
 *
 * 2. **超时靠注入的时钟 + `tick()` 推进，不靠 `setTimeout`。** 超时是这里最需要被验证的行为
 *    （"卡住了没有"是用户唯一能给出的判断），而真实定时器在测试里只能靠 sleep 等 ——
 *    既慢又不确定。`tick()` 与 `MavlinkSession.tick()` 是同一个套路：
 *    生产环境由构造时的定时器自动调用（`autoTickMs`，0 表示关闭）。
 *
 * 3. **事件同步派发，返回值是最终结论。** 界面既需要过程中的"第几条/共几条"，
 *    也需要一个明确的结局（完成/失败/取消）——两者都给，而不是让界面自己从事件流里拼。
 *
 * 4. **同一任务类型上不允许并发传输。** 中途打断机体的上传状态机（重新发 `MISSION_COUNT`）
 *    会让真实固件回 `MAV_MISSION_ERROR`，"看起来在传"和"机体上的状态机"就此错位。
 *    所以第二次调用会明确回一个"已有传输在进行中"的失败，而不是静默顶掉、
 *    也不是排队（排队会让界面上的按钮点了没反应）。
 */
import { commandId, vehicleKindOf } from '@hgc/ardupilot';
import type { MessageValues } from '@hgc/mavlink';

import { createEventBus, type EventBus, type Unsubscribe } from '../events';
import type {
  LinkStateEvent,
  MissionAckEvent,
  MissionCountEvent,
  MissionItemEvent,
  MissionRequestEvent,
  Vehicle,
  VehicleState,
} from '../vehicle';

import { compareMissions, type MissionComparison } from './compare';
import { isMissionResultAccepted, missionResultReason } from './results';
import {
  MISSION_TYPE_MISSION,
  emptyPlan,
  isMissionTypeId,
  itemFromMissionItemEvent,
  missionItemNoun,
  missionTypeLabel,
  normalizePlan,
  wireMissionItemValues,
  type MissionHomePosition,
  type MissionItem,
  type MissionPlan,
  type MissionTypeId,
} from './types';
import {
  validateMission,
  type MissionValidationIssue,
  type MissionValidationOptions,
  type MissionValidationResult,
} from './validate';

/** 等机体索要下一条 / 等 `MISSION_ACK` 的时限 */
const DEFAULT_RESPONSE_TIMEOUT_MS = 1500;
/** 连续超时多少次之后放弃（每次超时会重发一次，所以总发送次数 = 它 + 1） */
const DEFAULT_MAX_RETRIES = 3;
/** 自动 tick 的间隔；0 = 不自动推进 */
const DEFAULT_AUTO_TICK_MS = 250;
/** 等 `HOME_POSITION` 的时限 */
const DEFAULT_HOME_TIMEOUT_MS = 2000;
/** 机体反复索要同一条的上限：超过它就不再重发，避免无限循环把链路刷满 */
const MAX_DUPLICATE_FACTOR = 5;

/**
 * `MAV_CMD_DO_SET_HOME` 的编号。
 *
 * 从 `@hgc/ardupilot` 的命令表取；表坏了才退回历史常量 179 ——
 * 设错返航点是能让飞机飞到别处降落的错误，宁可写死一个正确值也不接受 `undefined`。
 */
const MAV_CMD_DO_SET_HOME = commandId('MAV_CMD_DO_SET_HOME') ?? 179;

export type MissionDirection = 'upload' | 'download';
export type MissionTransferOutcome = 'completed' | 'failed' | 'cancelled';

/**
 * 失败原因的分类码。界面按它决定"要不要给重试按钮"，
 * `reason` 决定"给用户看什么字"——两者不混在一起。
 */
export type MissionFailureCode =
  | 'busy'
  | 'disposed'
  | 'link-closed'
  | 'link-down'
  | 'validation'
  | 'timeout'
  | 'rejected'
  | 'protocol'
  | 'send-failed'
  | 'cancelled';

/** 进度：界面上唯一能让用户判断"是不是卡住了"的信息 */
export interface MissionTransferProgress {
  readonly missionType: MissionTypeId;
  readonly direction: MissionDirection;
  /** 已完成（上传 = 已发出，下载 = 已收到）的条数 */
  readonly completed: number;
  readonly total: number;
  /** 正在处理的那一条在界面上的序号（从 1 开始）；还没开始为 0 */
  readonly current: number;
  /** 累计重发次数（含超时重发与"机体重复索要"引起的重发） */
  readonly retries: number;
}

export interface MissionTransferResult {
  readonly missionType: MissionTypeId;
  readonly direction: MissionDirection;
  readonly outcome: MissionTransferOutcome;
  readonly completed: number;
  readonly total: number;
  /** 失败或取消时的中文原因；成功为 null */
  readonly reason: string | null;
  readonly code: MissionFailureCode | null;
  /** 机体的 `MISSION_ACK` 结果码（`MAV_MISSION_RESULT`）；没收到为 null */
  readonly ackCode: number | null;
  /** 上传时是"我们发出去的"，下载时是"从机体读回来的" */
  readonly plan: MissionPlan;
  /** 这次传输是否全部走 `MISSION_ITEM_INT`（float 版会丢精度，UI 应提示） */
  readonly int: boolean;
}

export interface MissionUploadStartedEvent {
  readonly missionType: MissionTypeId;
  readonly total: number;
  readonly at: number;
}

export interface MissionUploadProgressEvent extends MissionTransferProgress {
  readonly at: number;
}

export interface MissionUploadCompleteEvent {
  readonly missionType: MissionTypeId;
  readonly total: number;
  readonly at: number;
}

export interface MissionUploadFailedEvent {
  readonly missionType: MissionTypeId;
  readonly total: number;
  readonly completed: number;
  readonly reason: string;
  readonly code: MissionFailureCode;
  readonly ackCode: number | null;
  readonly at: number;
}

export interface MissionDownloadStartedEvent {
  readonly missionType: MissionTypeId;
  readonly at: number;
}

export interface MissionDownloadProgressEvent extends MissionTransferProgress {
  readonly at: number;
}

export interface MissionDownloadCompleteEvent {
  readonly missionType: MissionTypeId;
  readonly total: number;
  readonly int: boolean;
  readonly at: number;
}

export interface MissionDownloadFailedEvent {
  readonly missionType: MissionTypeId;
  readonly total: number;
  readonly completed: number;
  readonly reason: string;
  readonly code: MissionFailureCode;
  readonly ackCode: number | null;
  readonly at: number;
}

export interface MissionValidationFailedEvent {
  readonly missionType: MissionTypeId;
  readonly issues: readonly MissionValidationIssue[];
  readonly summary: string;
  readonly at: number;
}

export interface MissionCancelledEvent {
  readonly missionType: MissionTypeId;
  readonly direction: MissionDirection;
  readonly completed: number;
  readonly total: number;
  readonly reason: string;
  readonly at: number;
}

/** 机体上的任务快照变了（下载完成，或上传成功后我们用本地方案更新了它） */
export interface MissionRemoteChangedEvent {
  readonly missionType: MissionTypeId;
  readonly total: number;
  readonly source: MissionDirection;
  readonly at: number;
}

export type MissionEvents = {
  uploadStarted: MissionUploadStartedEvent;
  uploadProgress: MissionUploadProgressEvent;
  uploadComplete: MissionUploadCompleteEvent;
  uploadFailed: MissionUploadFailedEvent;
  downloadStarted: MissionDownloadStartedEvent;
  downloadProgress: MissionDownloadProgressEvent;
  downloadComplete: MissionDownloadCompleteEvent;
  downloadFailed: MissionDownloadFailedEvent;
  validationFailed: MissionValidationFailedEvent;
  cancelled: MissionCancelledEvent;
  remoteChanged: MissionRemoteChangedEvent;
};

export interface MissionManagerOptions {
  readonly vehicle: Vehicle;
  /** 时钟。默认 `Date.now`；测试里注入假时钟以获得确定性 */
  readonly now?: () => number;
  readonly responseTimeoutMs?: number;
  readonly maxRetries?: number;
  /** 自动 tick 间隔（毫秒）；0 表示不自动推进，由调用方手动 `tick()` */
  readonly autoTickMs?: number;
  /** 条数上限，默认与 ArduPilot 的 `MISSION_MAX_NUMBER` 一致 */
  readonly maxItems?: number;
  readonly onListenerError?: (error: unknown, type: string) => void;
}

interface TargetIds {
  readonly systemId: number;
  readonly componentId: number;
}

type Settle = (result: MissionTransferResult) => void;

interface UploadTransfer {
  readonly direction: 'upload';
  readonly missionType: MissionTypeId;
  readonly plan: MissionPlan;
  readonly items: readonly MissionItem[];
  readonly total: number;
  readonly target: TargetIds;
  /** 回显给我们的 `MISSION_ACK.opaque_id`，用来把回应和这一次上传对上 */
  readonly opaqueId: number;
  readonly sentSeqs: Set<number>;
  /** 最近一条发出去的 seq；-1 = 只发了 `MISSION_COUNT` */
  lastSentSeq: number;
  /** 最近一条是按 INT 还是 float 版发的（决定重发用哪个版本） */
  lastSentInt: boolean;
  /** 是否全程走 INT 版；机体要 float 版时置 false */
  allInt: boolean;
  /** 正在处理的那一条；-1 = 还没开始 */
  currentSeq: number;
  /** 当前等待阶段的起点（超时判断用） */
  waitingSince: number;
  /** 连续超时次数：每收到一次有效索要就清零 */
  retries: number;
  /** 累计重发次数（含重复索要导致的重发），用于进度显示 */
  resends: number;
  duplicates: number;
  invalidRequests: number;
  ackCode: number | null;
  readonly settle: Settle;
}

interface DownloadTransfer {
  readonly direction: 'download';
  readonly missionType: MissionTypeId;
  readonly target: TargetIds;
  /** 机体自报的条数；-1 = 还没收到 `MISSION_COUNT` */
  total: number;
  readonly received: Map<number, MissionItem>;
  /** 有几条来自 float 版 `MISSION_ITEM` */
  floatItems: number;
  /** 最近一次请求的 seq；-1 = 只发了 `MISSION_REQUEST_LIST` */
  lastRequestedSeq: number;
  waitingSince: number;
  retries: number;
  resends: number;
  invalidItems: number;
  ackCode: number | null;
  readonly settle: Settle;
}

type Transfer = UploadTransfer | DownloadTransfer;

interface HomeWait {
  readonly deadline: number;
  readonly resolve: (home: MissionHomePosition | null) => void;
  readonly off: Unsubscribe;
}

export class MissionManager {
  readonly events: EventBus<MissionEvents>;

  private readonly vehicle: Vehicle;
  private readonly now: () => number;
  private readonly responseTimeoutMs: number;
  private readonly maxRetries: number;
  private readonly maxItems: number | null;
  private readonly unsubscribes: Unsubscribe[] = [];
  private readonly transfers = new Map<MissionTypeId, Transfer>();
  private readonly remotes = new Map<MissionTypeId, MissionPlan>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private opaqueId = 0;
  private disposed = false;
  private homeWait: HomeWait | null = null;

  constructor(options: MissionManagerOptions) {
    this.vehicle = options.vehicle;
    this.now = options.now ?? (() => Date.now());
    this.responseTimeoutMs = options.responseTimeoutMs ?? DEFAULT_RESPONSE_TIMEOUT_MS;
    this.maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.maxItems = options.maxItems ?? null;
    this.events = createEventBus<MissionEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );

    const bus = this.vehicle.events;
    this.unsubscribes.push(
      bus.on('missionRequest', (event) => this.handleMissionRequest(event)),
      bus.on('missionCount', (event) => this.handleMissionCount(event)),
      bus.on('missionItem', (event) => this.handleMissionItem(event)),
      bus.on('missionAck', (event) => this.handleMissionAck(event)),
      // 链路断了/关了必须把在传的东西收尾：留着半套状态会让"重新上传"永远卡在 busy 上
      bus.on('linkState', (event) => this.handleLinkState(event)),
    );

    const autoTickMs = options.autoTickMs ?? DEFAULT_AUTO_TICK_MS;
    if (autoTickMs > 0) this.timer = setInterval(() => this.tick(), autoTickMs);
  }

  // -------------------------------------------------------------------------
  // 查询
  // -------------------------------------------------------------------------

  /** 某个任务类型上的传输进度；没有传输时返回 null */
  progress(missionType: MissionTypeId): MissionTransferProgress | null {
    const transfer = this.transfers.get(missionType);
    return transfer === undefined ? null : this.progressOf(transfer);
  }

  /** 有没有传输在跑（不传 missionType 时问"任意一个"） */
  isBusy(missionType?: MissionTypeId): boolean {
    return missionType === undefined ? this.transfers.size > 0 : this.transfers.has(missionType);
  }

  /**
   * 已知的"机体上的那份任务"。
   *
   * 来源有两个：下载成功后是机体给的原样数据；上传成功后是**我们发出去的那份**
   * （依据是机体 ACK 了完整条数）。后者是乐观记录 —— 固件可能自己做了规范化，
   * 想确认就重新下载一次，然后 `compareMissions`。
   */
  remotePlan(missionType: MissionTypeId = MISSION_TYPE_MISSION): MissionPlan | null {
    return this.remotes.get(missionType) ?? null;
  }

  /**
   * 本地方案与已知的机体状态是否一致；还不知道机体上是什么时返回 null
   * （界面据此显示"尚未与机体核对"，而不是显示"一致"）。
   */
  compareWithRemote(local: MissionPlan): MissionComparison | null {
    const remote = this.remotes.get(local.missionType);
    return remote === undefined ? null : compareMissions(local, remote);
  }

  /** 校验一份任务（纯函数转发，界面可以在编辑过程中实时调用） */
  validate(plan: MissionPlan, options: MissionValidationOptions = {}): MissionValidationResult {
    // 管理器自己限定的条数优先，否则用控制器里的默认值
    const maxItems = options.maxItems ?? this.maxItems;
    return validateMission(plan, maxItems === null ? options : { ...options, maxItems });
  }

  /** 本管理器自己事件总线上的订阅者数量（泄漏检查用；见 events.ts 的说明） */
  listenerCount(): number {
    return this.events.listenerCount();
  }

  // -------------------------------------------------------------------------
  // 上传
  // -------------------------------------------------------------------------

  /**
   * 把一份任务传到机体上。
   *
   * 流程：`MISSION_COUNT` → 机体逐条 `MISSION_REQUEST_INT` → 我们回 `MISSION_ITEM_INT`
   * → 全部收齐后机体回 `MISSION_ACK`。**只有 ACK 的结果码为 0 才算成功**；
   * 非 0 的结果码会带着中文原因失败返回，并且不更新"机体上的任务"这个已知量。
   *
   * `count = 0` 是合法操作（清空机体上的那份任务），不会被当成错误。
   */
  async upload(plan: MissionPlan): Promise<MissionTransferResult> {
    const normalized = normalizePlan(plan);
    const missionType = normalized.missionType;
    const at = this.now();

    if (this.disposed) return this.uploadFailureResult(normalized, 'disposed', '管理器已销毁，无法上传');
    if (!isMissionTypeId(missionType)) {
      return this.uploadFailureResult(
        { missionType: MISSION_TYPE_MISSION, items: [] },
        'protocol',
        `未知的任务类型 ${String(missionType)}`,
      );
    }
    if (this.transfers.has(missionType)) {
      return this.uploadFailureResult(
        normalized,
        'busy',
        `该${missionTypeLabel(missionType)}上已有传输在进行中（${this.describeBusy(missionType)}），先取消或等它结束`,
      );
    }

    const validation = this.validate(normalized, {
      kind: this.vehicleKind(),
      armed: this.vehicle.state.armed,
    });
    if (!validation.ok) {
      this.events.emit('validationFailed', {
        missionType,
        issues: validation.issues,
        summary: validation.summary,
        at,
      });
      return this.uploadFailureResult(normalized, 'validation', validation.summary);
    }

    if (this.vehicle.session.state === 'idle') {
      return this.uploadFailureResult(normalized, 'link-closed', '链路未打开，无法上传航线');
    }

    let settle!: Settle;
    const promise = new Promise<MissionTransferResult>((resolve) => {
      settle = resolve;
    });

    const transfer: UploadTransfer = {
      direction: 'upload',
      missionType,
      plan: normalized,
      items: normalized.items,
      total: normalized.items.length,
      target: { systemId: this.vehicle.systemId, componentId: this.vehicle.componentId },
      opaqueId: this.nextOpaqueId(),
      sentSeqs: new Set(),
      lastSentSeq: -1,
      lastSentInt: true,
      allInt: true,
      currentSeq: -1,
      waitingSince: at,
      retries: 0,
      resends: 0,
      duplicates: 0,
      invalidRequests: 0,
      ackCode: null,
      settle,
    };
    this.transfers.set(missionType, transfer);
    this.events.emit('uploadStarted', { missionType, total: transfer.total, at });

    const sent = await this.sendCount(transfer);
    // 发送可能已经把整个握手跑完了（模拟链路是同步回灌的），所以这里必须重新看一眼状态
    if (!sent && this.transfers.get(missionType) === transfer) {
      this.failUpload(transfer, 'send-failed', '发送 MISSION_COUNT 失败（链路可能刚断开）');
    }
    return promise;
  }

  // -------------------------------------------------------------------------
  // 下载
  // -------------------------------------------------------------------------

  /**
   * 把机体上的任务读回来。
   *
   * 流程：`MISSION_REQUEST_LIST` → 机体回 `MISSION_COUNT` → 我们逐条 `MISSION_REQUEST_INT`
   * → 机体回 `MISSION_ITEM_INT`（老固件可能回 float 版）→ 收齐后我们回 `MISSION_ACK`。
   * 返回的 `plan` 就是机体上那份任务；这也是"上传成功"唯一可靠的证据。
   */
  async download(missionType: number = MISSION_TYPE_MISSION): Promise<MissionTransferResult> {
    const at = this.now();
    const fallback = emptyPlan(MISSION_TYPE_MISSION);

    if (this.disposed) return this.downloadFailureResult(fallback, 'disposed', '管理器已销毁，无法下载');
    if (!isMissionTypeId(missionType)) {
      return this.downloadFailureResult(fallback, 'protocol', `未知的任务类型 ${missionType}`);
    }
    if (this.transfers.has(missionType)) {
      return this.downloadFailureResult(
        emptyPlan(missionType),
        'busy',
        `该${missionTypeLabel(missionType)}上已有传输在进行中（${this.describeBusy(missionType)}），先取消或等它结束`,
      );
    }
    if (this.vehicle.session.state === 'idle') {
      return this.downloadFailureResult(emptyPlan(missionType), 'link-closed', '链路未打开，无法下载航线');
    }

    let settle!: Settle;
    const promise = new Promise<MissionTransferResult>((resolve) => {
      settle = resolve;
    });

    const transfer: DownloadTransfer = {
      direction: 'download',
      missionType,
      target: { systemId: this.vehicle.systemId, componentId: this.vehicle.componentId },
      total: -1,
      received: new Map(),
      floatItems: 0,
      lastRequestedSeq: -1,
      waitingSince: at,
      retries: 0,
      resends: 0,
      invalidItems: 0,
      ackCode: null,
      settle,
    };
    this.transfers.set(missionType, transfer);
    this.events.emit('downloadStarted', { missionType, at });

    const sent = await this.sendRequestList(transfer);
    if (!sent && this.transfers.get(missionType) === transfer) {
      this.failDownload(transfer, 'send-failed', '发送 MISSION_REQUEST_LIST 失败（链路可能刚断开）');
    }
    return promise;
  }

  // -------------------------------------------------------------------------
  // 取消 / 销毁
  // -------------------------------------------------------------------------

  /**
   * 取消传输。不传 `missionType` 时取消全部。
   *
   * 刻意**不**给机体发 `MISSION_CLEAR_ALL`：取消的语义是"我不传了"，
   * 不是"把我的航线删掉"。而清空机体上已有任务的权限属于用户明确的那一次操作
   * （上传一份空航线），不能藏在"取消"按钮里。
   */
  cancel(missionType?: MissionTypeId): boolean {
    let cancelled = false;
    for (const transfer of [...this.transfers.values()]) {
      if (missionType !== undefined && transfer.missionType !== missionType) continue;
      this.cancelTransfer(transfer, '用户取消', 'cancelled');
      cancelled = true;
    }
    return cancelled;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    for (const transfer of [...this.transfers.values()]) {
      this.cancelTransfer(transfer, '管理器已销毁，传输中止', 'disposed');
    }
    this.finishHomeWait(null);
    for (const off of this.unsubscribes) off();
    this.unsubscribes.length = 0;
    this.events.clear();
  }

  // -------------------------------------------------------------------------
  // 返航点
  // -------------------------------------------------------------------------

  /**
   * 读返航点。
   *
   * `HOME_POSITION` 默认不推流，所以先让 `Vehicle.requestHome()` 去要一次（它自带节流），
   * 然后等状态里出现返航点。已经拿到过就直接返回缓存的 —— 每次读都重新请求
   * 会在界面上制造一串没有意义的链路流量。
   */
  async readHome(options: { readonly timeoutMs?: number } = {}): Promise<MissionHomePosition | null> {
    const cached = homeFromState(this.vehicle.state);
    if (cached !== null || this.disposed) return cached;
    const timeoutMs = options.timeoutMs ?? DEFAULT_HOME_TIMEOUT_MS;

    const requested = await this.vehicle.requestHome();
    const afterRequest = homeFromState(this.vehicle.state);
    if (afterRequest !== null) return afterRequest;
    // 被节流挡住或链路没打开：这一次读不到，交给下一次（避免在这里排队等一个不会到的回应）
    if (!requested || this.disposed) return null;

    return await this.waitForHome(timeoutMs);
  }

  /**
   * 设返航点。
   *
   * `'current'` = 用飞机当前位置（`param1 = 1`），这也是飞行中最常用的做法；
   * 显式坐标走 `param5/6/7`。返回值是"命令有没有发出去"，
   * **不是**"机体有没有接受" —— 后者要等 `COMMAND_ACK`，那是指令层的职责
   * （`Vehicle` 在收到成功的 ACK 时会清掉缓存的返航点并自动重新请求）。
   */
  async setHome(home: MissionHomePosition | 'current'): Promise<boolean> {
    if (this.disposed) return false;
    if (home !== 'current' && !isValidHome(home)) return false;
    const params =
      home === 'current'
        ? [1, 0, 0, 0, 0, 0, 0]
        : [0, 0, 0, 0, home.latDeg, home.lonDeg, home.altM];
    const sent = await this.vehicle.sendCommandLong(MAV_CMD_DO_SET_HOME, params);
    return sent;
  }

  // -------------------------------------------------------------------------
  // 时间推进
  // -------------------------------------------------------------------------

  /**
   * 推进超时判断。生产环境由构造函数里的定时器调用；
   * 测试里手动调用（配合假时钟），这样才能精确测出"重发了几次、什么时候放弃"。
   */
  tick(at: number = this.now()): void {
    if (this.disposed) return;
    for (const transfer of [...this.transfers.values()]) {
      if (transfer.direction === 'upload') this.tickUpload(transfer, at);
      else this.tickDownload(transfer, at);
    }
    this.tickHomeWait(at);
  }

  // -------------------------------------------------------------------------
  // 内部：出帧
  // -------------------------------------------------------------------------

  private vehicleKind(): ReturnType<typeof vehicleKindOf> {
    const type = this.vehicle.state.type;
    return type === null ? 'unknown' : vehicleKindOf(type);
  }

  private nextOpaqueId(): number {
    // 0 是 `opaque_id` 的"未提供"值，所以从 1 开始循环
    this.opaqueId = this.opaqueId >= 0xffffffff ? 1 : this.opaqueId + 1;
    return this.opaqueId;
  }

  /**
   * 发一条消息。
   *
   * 发送前让出一个微任务：调用点几乎都在事件回调里（收到 `missionRequest` 就回一条任务项），
   * 不让出的话发送会嵌在"正在解析这一帧"的同步栈里 —— 理由与 `Vehicle.sendCommandLong()`
   * 里的说明完全相同，只是这条路走的是通用 `send()`（MISSION_* 不是 `COMMAND_LONG`）。
   */
  private async sendMessage(name: string, values: MessageValues): Promise<boolean> {
    await Promise.resolve();
    if (this.disposed) return false;
    try {
      await this.vehicle.session.send(name, values);
      return true;
    } catch {
      return false;
    }
  }

  private async sendCount(transfer: UploadTransfer): Promise<boolean> {
    return this.sendMessage('MISSION_COUNT', {
      count: transfer.total,
      target_system: transfer.target.systemId,
      target_component: transfer.target.componentId,
      mission_type: transfer.missionType,
      // 回显这个值让我们能把回应和这一次上传对上（两次上传的结果码不会互相混淆）
      opaque_id: transfer.opaqueId,
    });
  }

  private async sendItem(transfer: UploadTransfer, seq: number, int: boolean): Promise<boolean> {
    const item = transfer.items[seq];
    if (item === undefined) return false;
    transfer.lastSentSeq = seq;
    transfer.lastSentInt = int;
    transfer.currentSeq = seq;
    if (!int) transfer.allInt = false;
    /*
     * 先记入"已发出"，再真正写出去。
     *
     * 链路是**同步回灌**的（内置模拟机就是），机体的下一次索要在 `await` 返回之前
     * 就已经到达了。那时如果还没记入，这个 seq 会被当成"新的一条"而不是"重复索要"，
     * 重发上限就形同虚设 —— 一台只会重复索要的固件能让这里无限发下去。
     */
    transfer.sentSeqs.add(seq);
    // 进度在发送**之前**发：界面上要显示的是"正在传第 N 条"，
    // 而最后一条发完往往紧接着就是 ACK，之后再发进度用户根本看不到
    this.emitUploadProgress(transfer);
    const sent = await this.sendMessage(
      int ? 'MISSION_ITEM_INT' : 'MISSION_ITEM',
      wireMissionItemValues(
        { ...item, seq },
        {
          int,
          missionType: transfer.missionType,
          targetSystem: transfer.target.systemId,
          targetComponent: transfer.target.componentId,
        },
      ),
    );
    if (!sent) transfer.sentSeqs.delete(seq);
    return sent;
  }

  private async sendRequestList(transfer: DownloadTransfer): Promise<boolean> {
    return this.sendMessage('MISSION_REQUEST_LIST', {
      target_system: transfer.target.systemId,
      target_component: transfer.target.componentId,
      mission_type: transfer.missionType,
    });
  }

  private async sendRequestItem(transfer: DownloadTransfer, seq: number): Promise<boolean> {
    transfer.lastRequestedSeq = seq;
    this.emitDownloadProgress(transfer);
    return this.sendMessage('MISSION_REQUEST_INT', {
      seq,
      target_system: transfer.target.systemId,
      target_component: transfer.target.componentId,
      mission_type: transfer.missionType,
    });
  }

  private async sendDownloadAck(transfer: DownloadTransfer): Promise<boolean> {
    return this.sendMessage('MISSION_ACK', {
      target_system: transfer.target.systemId,
      target_component: transfer.target.componentId,
      type: 0,
      mission_type: transfer.missionType,
    });
  }

  // -------------------------------------------------------------------------
  // 内部：机体事件的匹配
  // -------------------------------------------------------------------------

  /**
   * 找到一个能处理该 `mission_type` 的传输。
   *
   * 兼容性处理：`mission_type` 是 MAVLink 2 的**扩展字段**，老固件（或只认识基础字段的
   * 发送方）根本不会带上它，此时解码出来的就是 0 —— 与"真的是航线（0）"无法区分。
   * 所以只在"确实没有任何 0 号任务在传、且只有一条传输在跑"时，才把 0 认作那一条。
   * 有多个候选时宁可不动：`mission_type` 认错会让围栏的项被写进航线里，
   * 那是"两边的任务都被改坏"，比"这一次传输超时"严重得多。
   */
  private transferFor(missionType: number): Transfer | null {
    if (!isMissionTypeId(missionType)) return null;
    const exact = this.transfers.get(missionType);
    if (exact !== undefined) return exact;
    if (missionType !== MISSION_TYPE_MISSION) return null;
    const candidates = [...this.transfers.values()];
    return candidates.length === 1 ? (candidates[0] ?? null) : null;
  }

  private handleMissionRequest(event: MissionRequestEvent): void {
    const transfer = this.uploadFor(event.missionType);
    if (transfer === null) return;

    if (!Number.isInteger(event.seq) || event.seq < 0 || event.seq >= transfer.total) {
      transfer.invalidRequests += 1;
      if (transfer.invalidRequests > this.maxRetries) {
        this.failUpload(
          transfer,
          'protocol',
          `机体反复索要越界的序号 ${event.seq}（本次只有 ${transfer.total} 条），已忽略 ${transfer.invalidRequests} 次后放弃`,
        );
      }
      return;
    }

    /*
     * 重复索要是**正常现象**：无线链路上"机体发出的下一条请求"丢掉的概率不低，
     * 而机体的超时比链路层短，它就会再要一次同一条。
     * 所以这里重发那一条，而不是报"协议错乱"——报错的表现会是"链路一抖就上传失败"，
     * 而真正的原因（请求丢了一帧）在界面和日志里都看不出来。
     */
    if (transfer.sentSeqs.has(event.seq)) {
      transfer.duplicates += 1;
      transfer.resends += 1;
      /*
       * 但重复索要也要有上限。没有上限的话，一台"只要没收到 ACK 就一直重发请求"的
       * 固件会把这条链路刷满，而地面上看到的是"界面卡在 1/4 条"——
       * 有上限至少能给出一个明确的中文结论。
       */
      if (transfer.duplicates > this.maxRetries * MAX_DUPLICATE_FACTOR) {
        this.failUpload(
          transfer,
          'protocol',
          `机体连续 ${transfer.duplicates} 次索要第 ${event.seq + 1} 个${missionItemNoun(transfer.missionType)}（已重发多次仍未被接受），放弃本次上传`,
        );
        return;
      }
    }

    // 机体的任何一次有效索要都是"有进展"：重新计时，并把连续超时计数清零
    transfer.waitingSince = this.now();
    transfer.retries = 0;
    void this.sendItem(transfer, event.seq, event.int);
  }

  private handleMissionCount(event: MissionCountEvent): void {
    const transfer = this.downloadFor(event.missionType);
    if (transfer === null || transfer.total >= 0) return;
    transfer.total = event.count;
    transfer.waitingSince = this.now();
    transfer.retries = 0;

    if (event.count === 0) {
      // 空任务是合法的（机体上确实没有这份任务），直接收尾
      this.completeDownload(transfer);
      return;
    }
    void this.sendRequestItem(transfer, 0);
  }

  private handleMissionItem(event: MissionItemEvent): void {
    const transfer = this.downloadFor(event.missionType);
    if (transfer === null || transfer.total < 0) return;

    if (event.seq < 0 || event.seq >= transfer.total) {
      transfer.invalidItems += 1;
      if (transfer.invalidItems > this.maxRetries) {
        this.failDownload(
          transfer,
          'protocol',
          `机体发来了越界的任务项 seq=${event.seq}（本次共 ${transfer.total} 条），已忽略 ${transfer.invalidItems} 次后放弃`,
        );
      }
      return;
    }

    if (!event.int) transfer.floatItems += 1;
    if (!transfer.received.has(event.seq)) {
      transfer.received.set(event.seq, itemFromMissionItemEvent(event));
    }
    transfer.waitingSince = this.now();
    transfer.retries = 0;

    if (transfer.received.size >= transfer.total) {
      this.completeDownload(transfer);
      return;
    }
    /*
     * 按"最小的缺失序号"往下走，而不是简单地 seq+1：
     * 真实链路上任务项可能乱序到达（重传、多路复用），只有这样才能收敛到收齐。
     */
    const missing = this.firstMissingSeq(transfer);
    if (missing === null) {
      this.completeDownload(transfer);
      return;
    }
    void this.sendRequestItem(transfer, missing);
  }

  private handleMissionAck(event: MissionAckEvent): void {
    const upload = this.uploadFor(event.missionType);
    if (upload !== null) {
      // `opaque_id` 回显：不为 0 且与本次不同，说明这条 ACK 讲的是另一次上传（老固件填 0）
      if (event.opaqueId !== 0 && event.opaqueId !== upload.opaqueId) return;
      upload.ackCode = event.type;
      if (isMissionResultAccepted(event.type)) {
        this.completeUpload(upload);
      } else {
        this.failUpload(
          upload,
          'rejected',
          `机体拒绝了${missionTypeLabel(upload.missionType)}：${missionResultReason(event.type)}`,
        );
      }
      return;
    }

    const download = this.downloadFor(event.missionType);
    if (download !== null && !isMissionResultAccepted(event.type)) {
      // 下载中的非 0 ACK 是机体在说"这次下载不继续了"（例如越界索要、任务被清空）
      download.ackCode = event.type;
      this.failDownload(
        download,
        'rejected',
        `机体中止了${missionTypeLabel(download.missionType)}的下载：${missionResultReason(event.type)}`,
      );
    }
    // 下载中的 0 号 ACK 忽略：那是我们自己发出去的那条，链路不会回灌给自己
  }

  private handleLinkState(change: LinkStateEvent): void {
    if (change.state === 'online' || change.state === 'silent' || change.state === 'connecting') return;
    const reason =
      change.state === 'offline'
        ? '链路已断开（心跳超时），传输中止'
        : change.state === 'error'
          ? '链路出错，传输中止'
          : '链路已关闭，传输中止';
    for (const transfer of [...this.transfers.values()]) {
      this.cancelTransfer(transfer, reason, 'link-down');
    }
  }

  private uploadFor(missionType: number): UploadTransfer | null {
    const transfer = this.transferFor(missionType);
    return transfer !== null && transfer.direction === 'upload' ? transfer : null;
  }

  private downloadFor(missionType: number): DownloadTransfer | null {
    const transfer = this.transferFor(missionType);
    return transfer !== null && transfer.direction === 'download' ? transfer : null;
  }

  private firstMissingSeq(transfer: DownloadTransfer): number | null {
    for (let seq = 0; seq < transfer.total; seq += 1) {
      if (!transfer.received.has(seq)) return seq;
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // 内部：超时与重发
  // -------------------------------------------------------------------------

  private tickUpload(transfer: UploadTransfer, at: number): void {
    if (at - transfer.waitingSince < this.responseTimeoutMs) return;
    if (transfer.retries >= this.maxRetries) {
      const noun = missionItemNoun(transfer.missionType);
      const reason =
        transfer.lastSentSeq < 0
          ? `机体在 ${this.responseTimeoutMs} 毫秒内没有索要第 1 个${noun}（共 ${transfer.total} 条），重发 ${transfer.retries} 次后放弃`
          : `机体在 ${this.responseTimeoutMs} 毫秒内没有索要下一个${noun}（已发出 ${transfer.sentSeqs.size}/${transfer.total} 条），重发 ${transfer.retries} 次后放弃`;
      this.failUpload(transfer, 'timeout', reason);
      return;
    }
    transfer.retries += 1;
    transfer.resends += 1;
    transfer.waitingSince = at;
    // 还没开始（只发了 MISSION_COUNT）就重发 COUNT，否则重发最后那一条：
    // 重发"最后一条"在真实链路上是收敛的（机体收到重复项会重新索要下一条）
    if (transfer.lastSentSeq < 0) void this.sendCount(transfer);
    else void this.sendItem(transfer, transfer.lastSentSeq, transfer.lastSentInt);
  }

  private tickDownload(transfer: DownloadTransfer, at: number): void {
    if (at - transfer.waitingSince < this.responseTimeoutMs) return;
    if (transfer.retries >= this.maxRetries) {
      const reason =
        transfer.total < 0
          ? `机体在 ${this.responseTimeoutMs} 毫秒内没有回答 MISSION_COUNT，重发 ${transfer.retries} 次后放弃`
          : `机体在 ${this.responseTimeoutMs} 毫秒内没有发来第 ${transfer.lastRequestedSeq + 1} 条（已收到 ${transfer.received.size}/${transfer.total} 条，重发 ${transfer.retries} 次后放弃）`;
      this.failDownload(transfer, 'timeout', reason);
      return;
    }
    transfer.retries += 1;
    transfer.resends += 1;
    transfer.waitingSince = at;
    if (transfer.total < 0) void this.sendRequestList(transfer);
    else void this.sendRequestItem(transfer, Math.max(0, transfer.lastRequestedSeq));
  }

  // -------------------------------------------------------------------------
  // 内部：进度与收尾
  // -------------------------------------------------------------------------

  private progressOf(transfer: Transfer): MissionTransferProgress {
    if (transfer.direction === 'upload') {
      return {
        missionType: transfer.missionType,
        direction: 'upload',
        completed: transfer.sentSeqs.size,
        total: transfer.total,
        current: transfer.currentSeq < 0 ? 0 : transfer.currentSeq + 1,
        retries: transfer.resends,
      };
    }
    const total = transfer.total < 0 ? 0 : transfer.total;
    return {
      missionType: transfer.missionType,
      direction: 'download',
      completed: transfer.received.size,
      total,
      current: transfer.lastRequestedSeq < 0 ? 0 : transfer.lastRequestedSeq + 1,
      retries: transfer.resends,
    };
  }

  private emitUploadProgress(transfer: UploadTransfer): void {
    if (this.transfers.get(transfer.missionType) !== transfer) return;
    this.events.emit('uploadProgress', { ...this.progressOf(transfer), at: this.now() });
  }

  private emitDownloadProgress(transfer: DownloadTransfer): void {
    if (this.transfers.get(transfer.missionType) !== transfer) return;
    this.events.emit('downloadProgress', { ...this.progressOf(transfer), at: this.now() });
  }

  private completeUpload(transfer: UploadTransfer): void {
    const at = this.now();
    const completed = transfer.total;
    this.transfers.delete(transfer.missionType);
    /*
     * 机体 ACK 了完整条数 → 把本地方案记为"机体上的那份"。
     * 这是乐观记录：固件可能对坐标做了取整或补全，想确认就重新下载比对。
     * 但它比"什么都不记"有用得多 —— 它让"传上去了没有"这个问题立刻有个初步答案。
     */
    this.remotes.set(transfer.missionType, transfer.plan);
    this.events.emit('uploadComplete', { missionType: transfer.missionType, total: completed, at });
    this.events.emit('remoteChanged', {
      missionType: transfer.missionType,
      total: completed,
      source: 'upload',
      at,
    });
    transfer.settle({
      missionType: transfer.missionType,
      direction: 'upload',
      outcome: 'completed',
      completed,
      total: completed,
      reason: null,
      code: null,
      ackCode: transfer.ackCode,
      plan: transfer.plan,
      int: transfer.allInt,
    });
  }

  private failUpload(transfer: UploadTransfer, code: MissionFailureCode, reason: string): void {
    const completed = transfer.sentSeqs.size;
    this.transfers.delete(transfer.missionType);
    /*
     * 刻意**不**更新 remotes：失败的上传等于"机体上还是旧的那份"。
     * 把它标成"已上传"会让界面回答"传上去了"，而实际机体上可能是半份或旧航线 ——
     * 这正是本模块要防的那类错误。
     */
    this.events.emit('uploadFailed', {
      missionType: transfer.missionType,
      total: transfer.total,
      completed,
      reason,
      code,
      ackCode: transfer.ackCode,
      at: this.now(),
    });
    transfer.settle({
      missionType: transfer.missionType,
      direction: 'upload',
      outcome: 'failed',
      completed,
      total: transfer.total,
      reason,
      code,
      ackCode: transfer.ackCode,
      plan: transfer.plan,
      int: transfer.allInt,
    });
  }

  private completeDownload(transfer: DownloadTransfer): void {
    const at = this.now();
    const total = transfer.total < 0 ? 0 : transfer.total;
    const items = [...transfer.received.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, item], index) => (item.seq === index ? item : { ...item, seq: index }));
    const plan: MissionPlan = { missionType: transfer.missionType, items };
    this.transfers.delete(transfer.missionType);
    this.remotes.set(transfer.missionType, plan);
    // 收妥之后必须由地面站回一条 ACK：机体的下载游标靠它复位
    void this.sendDownloadAck(transfer);
    this.events.emit('downloadComplete', {
      missionType: transfer.missionType,
      total,
      int: transfer.floatItems === 0,
      at,
    });
    this.events.emit('remoteChanged', {
      missionType: transfer.missionType,
      total,
      source: 'download',
      at,
    });
    transfer.settle({
      missionType: transfer.missionType,
      direction: 'download',
      outcome: 'completed',
      completed: items.length,
      total,
      reason: null,
      code: null,
      ackCode: transfer.ackCode,
      plan,
      int: transfer.floatItems === 0,
    });
  }

  private failDownload(transfer: DownloadTransfer, code: MissionFailureCode, reason: string): void {
    const total = transfer.total < 0 ? 0 : transfer.total;
    const items = [...transfer.received.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, item], index) => (item.seq === index ? item : { ...item, seq: index }));
    this.transfers.delete(transfer.missionType);
    this.events.emit('downloadFailed', {
      missionType: transfer.missionType,
      total,
      completed: items.length,
      reason,
      code,
      ackCode: transfer.ackCode,
      at: this.now(),
    });
    transfer.settle({
      missionType: transfer.missionType,
      direction: 'download',
      outcome: 'failed',
      completed: items.length,
      total,
      reason,
      code,
      ackCode: transfer.ackCode,
      // 没读全的那份**不**交给调用方当"机体上的任务"：半份数据比没有数据更危险
      plan: { missionType: transfer.missionType, items: [] },
      int: transfer.floatItems === 0,
    });
  }

  /**
   * 取消：状态清干净、订阅不动（订阅属于管理器本身的生命周期，不属于一次传输），
   * 事件与返回值都明确说"这是取消"而不是"这是失败" ——
   * 用户点了取消之后弹一个"上传失败"的红条，是会用错操作员判断的。
   */
  private cancelTransfer(transfer: Transfer, reason: string, code: MissionFailureCode): void {
    const at = this.now();
    if (transfer.direction === 'upload') {
      const completed = transfer.sentSeqs.size;
      this.transfers.delete(transfer.missionType);
      this.events.emit('cancelled', {
        missionType: transfer.missionType,
        direction: 'upload',
        completed,
        total: transfer.total,
        reason,
        at,
      });
      transfer.settle({
        missionType: transfer.missionType,
        direction: 'upload',
        outcome: 'cancelled',
        completed,
        total: transfer.total,
        reason,
        code,
        ackCode: transfer.ackCode,
        plan: transfer.plan,
        int: transfer.allInt,
      });
      return;
    }
    const total = transfer.total < 0 ? 0 : transfer.total;
    const completed = transfer.received.size;
    this.transfers.delete(transfer.missionType);
    this.events.emit('cancelled', {
      missionType: transfer.missionType,
      direction: 'download',
      completed,
      total,
      reason,
      at,
    });
    transfer.settle({
      missionType: transfer.missionType,
      direction: 'download',
      outcome: 'cancelled',
      completed,
      total,
      reason,
      code,
      ackCode: transfer.ackCode,
      plan: emptyPlan(transfer.missionType),
      int: transfer.floatItems === 0,
    });
  }

  private uploadFailureResult(
    plan: MissionPlan,
    code: MissionFailureCode,
    reason: string,
  ): MissionTransferResult {
    return {
      missionType: plan.missionType,
      direction: 'upload',
      outcome: 'failed',
      completed: 0,
      total: plan.items.length,
      reason,
      code,
      ackCode: null,
      plan,
      int: true,
    };
  }

  private downloadFailureResult(
    plan: MissionPlan,
    code: MissionFailureCode,
    reason: string,
  ): MissionTransferResult {
    return {
      missionType: plan.missionType,
      direction: 'download',
      outcome: 'failed',
      completed: 0,
      total: 0,
      reason,
      code,
      ackCode: null,
      plan,
      int: true,
    };
  }

  private describeBusy(missionType: MissionTypeId): string {
    const progress = this.progress(missionType);
    if (progress === null) return '传输刚结束';
    const direction = progress.direction === 'upload' ? '上传' : '下载';
    return progress.total === 0
      ? `${direction}中`
      : `${direction}第 ${progress.current}/${progress.total} 条`;
  }

  // -------------------------------------------------------------------------
  // 内部：返航点等待
  // -------------------------------------------------------------------------

  private waitForHome(timeoutMs: number): Promise<MissionHomePosition | null> {
    return new Promise<MissionHomePosition | null>((resolve) => {
      // 车辆没有单独的"返航点到了"事件（它在状态快照里），所以监听 state 变化，
      // 同时由 tick() 兜住"超时/状态事件恰好错过"的情况
      const off = this.vehicle.events.on('state', () => {
        const home = homeFromState(this.vehicle.state);
        if (home !== null) this.finishHomeWait(home);
      });
      this.homeWait = { deadline: this.now() + timeoutMs, resolve, off };
    });
  }

  private tickHomeWait(at: number): void {
    const wait = this.homeWait;
    if (wait === null) return;
    const home = homeFromState(this.vehicle.state);
    if (home !== null) {
      this.finishHomeWait(home);
      return;
    }
    if (at >= wait.deadline) this.finishHomeWait(null);
  }

  private finishHomeWait(home: MissionHomePosition | null): void {
    const wait = this.homeWait;
    if (wait === null) return;
    this.homeWait = null;
    wait.off();
    wait.resolve(home);
  }
}

function homeFromState(state: VehicleState): MissionHomePosition | null {
  if (state.homeLatDeg === null || state.homeLonDeg === null || state.homeAltM === null) return null;
  return { latDeg: state.homeLatDeg, lonDeg: state.homeLonDeg, altM: state.homeAltM };
}

function isValidHome(home: MissionHomePosition): boolean {
  return (
    Number.isFinite(home.latDeg) &&
    Number.isFinite(home.lonDeg) &&
    Number.isFinite(home.altM) &&
    Math.abs(home.latDeg) <= 90 &&
    Math.abs(home.lonDeg) <= 180
  );
}
